import { useCallback, useEffect, useMemo, useState } from 'react';
import { bffQuery } from '../lib/bffClient';
import { useTranslation } from '../i18n';
import { CLINIC_TYPE_LABELS, TYPE_GROUP_LABELS } from '../screens/Clinic/Clinic.shared';

/**
 * The clinic-type registry — «نوع کلینیک» — as the server defines it.
 *
 * This used to be a fourteen-key constant in `Clinic.shared.js` while the server
 * stored an unvalidated string, and the two drifted the moment the registration
 * wizard started writing four uppercase words of its own. The vocabulary is now
 * server-owned: the wizard, the settings form and the service catalogue all read
 * the same list, and `clinicmanagement` refuses a key that is not in it.
 *
 * ── Why the label is still translated locally ────────────────────────────────
 *
 * The registry ships a Persian and an English label, and that is one language
 * short: the panel also runs in Arabic. So the LABEL comes from this app's own
 * catalogue whenever it has a key for it, and falls back to the server's Persian
 * only for a type added to the registry ahead of the client. That fallback is
 * what makes adding a type a server-side change; translating it is what makes it
 * a good one.
 */
const CLINIC_TYPES_QUERY = `
  query ClinicTypes {
    clinicTypes { key label labelEn group }
  }
`;

/**
 * The registry as this build knows it — `clinic_type_data.py` in clinicmanagement,
 * key for key, group for group, in its sort order.
 *
 * Used whenever the server has not answered with a list: while the query is in
 * flight, and when it fails (the BFF answers `[]` rather than throwing). Without it
 * an empty answer DISABLED «نوع کلینیک» in the registration wizard, so the one
 * choice the product branches on was locked to «عمومی» for anyone whose lookup was
 * slow or refused. Every key here is one the server validates, so picking from the
 * fallback can never produce a create the server rejects.
 */
const FALLBACK_TYPES = [
  ['general', 'general'], ['specialty', 'general'],
  ['dental', 'specialty'], ['ophthalmology', 'specialty'], ['dermatology', 'specialty'],
  ['pediatric', 'specialty'], ['cardiology', 'specialty'], ['orthopedic', 'specialty'],
  ['neurology', 'specialty'], ['gynecology', 'specialty'], ['internal', 'specialty'],
  ['ent', 'specialty'], ['urology', 'specialty'], ['psychology', 'specialty'],
  ['aesthetic', 'specialty'],
  ['physiotherapy', 'paraclinic'], ['rehabilitation', 'paraclinic'],
  ['laboratory', 'paraclinic'], ['radiology', 'paraclinic'], ['pharmacy', 'paraclinic'],
].map(([key, group]) => ({ key, group, label: '', labelEn: '' }));

export function useClinicTypes() {
  const { t } = useTranslation();
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    bffQuery(CLINIC_TYPES_QUERY, {}, 'ClinicTypes')
      .then((d) => { if (!cancelled) { setTypes(d?.clinicTypes || []); setError(null); } })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const labels = useMemo(() => CLINIC_TYPE_LABELS(t), [t]);

  // The server's list when it has one, else this build's copy of it.
  const effective = types.length ? types : FALLBACK_TYPES;

  /** The registry as `Select` options, grouped and in the server's order. */
  const options = useMemo(
    () => effective.map((x) => ({
      value: x.key,
      label: labels[x.key] || x.label || x.key,
      group: x.group,
      groupLabel: TYPE_GROUP_LABELS(t)[x.group] || x.group,
    })),
    [effective, labels, t],
  );

  /**
   * The label for a stored key.
   *
   * Falls back through the registry's own Persian label to the raw key, and never
   * to a generic word: a clinic whose type this build has not heard of should
   * read as ITS type, not as «کلینیک».
   */
  const labelFor = useCallback((key) => {
    if (!key) return '';
    return labels[key] || types.find((x) => x.key === key)?.label || key;
  }, [labels, types]);

  return { types, options, labelFor, loading, error };
}

export default useClinicTypes;
